/**
 * Month-by-month actionable tasks for each grass type.
 * Used by the Quick Start box at the top of each guide.
 * Index 0 = January, 11 = December.
 *
 * Sources: Cross-referenced against university cooperative extension programs
 * including Purdue, Penn State, UMN, NC State, Virginia Tech, Clemson, Texas A&M,
 * UF IFAS, UGA, Kansas State, Nebraska Extension, and others. Last verified March 2026.
 */

interface MonthlyTask {
  task: string
  priority: 'high' | 'medium' | 'low'
  toolLink?: string // optional link to a tool
}

// Helper to get current month index (0-11)
export function getCurrentMonthIndex(): number {
  return new Date().getMonth()
}

export function getMonthName(index: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return months[index] || 'January'
}

export function getSeasonLabel(monthIndex: number): string {
  if (monthIndex >= 2 && monthIndex <= 4) return 'Spring'
  if (monthIndex >= 5 && monthIndex <= 7) return 'Summer'
  if (monthIndex >= 8 && monthIndex <= 10) return 'Fall'
  return 'Winter'
}

const MONTHLY_TASKS: Record<string, MonthlyTask[][]> = {
  'Kentucky Bluegrass': [
    // January
    [
      { task: 'Minimize foot traffic on frozen turf', priority: 'high' },
      { task: 'Sharpen mower blades and service equipment', priority: 'medium' },
      { task: 'Order seed and fertilizer for spring', priority: 'medium' },
      { task: 'Apply lime if fall soil test showed low pH (if ground permits)', priority: 'low' },
    ],
    // February
    [
      { task: 'Continue staying off frozen turf', priority: 'high' },
      { task: 'Watch for early signs of snow mold as snow melts', priority: 'medium' },
      { task: 'Plan your spring pre-emergent timing', priority: 'medium' },
      { task: 'Check soil temperature weekly as it warms', priority: 'low', toolLink: '/tools/soil-temperature' },
    ],
    // March
    [
      { task: 'Apply pre-emergent when soil temps reach 50-55°F for several consecutive days', priority: 'high', toolLink: '/tools/herbicide-timing' },
      { task: 'Light raking to remove winter debris', priority: 'medium' },
      { task: 'Check irrigation system for leaks', priority: 'medium' },
      { task: 'Begin mowing when grass is actively growing', priority: 'low' },
    ],
    // April
    [
      { task: 'Begin regular mowing at 2.5-3.5 inches', priority: 'high' },
      { task: 'Spot-treat broadleaf weeds', priority: 'medium' },
      { task: 'Start irrigation if rainfall drops below 1 inch/week', priority: 'medium', toolLink: '/tools/watering-schedule' },
      { task: 'Hold off on fertilizer until spring growth flush slows (late April to May)', priority: 'medium' },
    ],
    // May
    [
      { task: 'Apply spring fertilizer after growth flush slows (0.75-1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Apply grub preventer (chlorantraniliprole products)', priority: 'medium' },
      { task: 'Mow at 3-3.5 inches, never removing more than 1/3', priority: 'medium' },
      { task: 'Monitor for pest activity', priority: 'low' },
    ],
    // June
    [
      { task: 'Raise mowing height to 3-3.5 inches for summer', priority: 'high' },
      { task: 'Water deeply 1-1.5 inches per week', priority: 'high', toolLink: '/tools/watering-schedule' },
      { task: 'Stop nitrogen fertilization for summer', priority: 'high' },
      { task: 'Watch for brown patch, dollar spot, and summer patch', priority: 'medium' },
    ],
    // July
    [
      { task: 'Maintain 3-3.5 inch mowing height', priority: 'high' },
      { task: 'Water early morning only, avoid evening watering', priority: 'high' },
      { task: 'Use iron for color if needed (not nitrogen)', priority: 'medium' },
      { task: 'Minimize heavy traffic on stressed turf', priority: 'medium' },
    ],
    // August
    [
      { task: 'Continue summer survival care', priority: 'high' },
      { task: 'Start planning fall overseeding and aeration', priority: 'medium' },
      { task: 'Watch soil temps as they begin cooling', priority: 'medium', toolLink: '/tools/soil-temperature' },
      { task: 'Order fall seed and fertilizer', priority: 'low' },
    ],
    // September
    [
      { task: 'Core aerate and overseed (MOST IMPORTANT month)', priority: 'high', toolLink: '/tools/aeration-calculator' },
      { task: 'Apply fall fertilizer at seeding (1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Lower mowing back to 2.5-3 inches', priority: 'medium' },
      { task: 'Continue watering, especially for new seed', priority: 'medium', toolLink: '/tools/watering-schedule' },
    ],
    // October
    [
      { task: 'Continue watering overseeded areas', priority: 'high' },
      { task: 'First mow of new seedlings when they reach 3.5 inches', priority: 'medium' },
      { task: 'Begin leaf removal', priority: 'medium' },
      { task: 'If you did NOT overseed and have Poa annua problems, apply fall pre-emergent', priority: 'low' },
    ],
    // November
    [
      { task: 'Apply winterizer fertilizer (1 lb N/1,000 sq ft quick-release urea)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Continue mowing at 2.5-3 inches until growth stops', priority: 'high' },
      { task: 'Remove all fallen leaves', priority: 'medium' },
      { task: 'Winterize irrigation system', priority: 'medium' },
    ],
    // December
    [
      { task: 'Lawn is dormant. Minimize foot traffic', priority: 'high' },
      { task: 'Keep leaves and debris off the lawn', priority: 'medium' },
      { task: 'Review the year and plan improvements for spring', priority: 'low' },
      { task: 'Enjoy the off-season', priority: 'low' },
    ],
  ],

  'Tall Fescue': [
    // January
    [
      { task: 'Stay off frozen turf to prevent crown damage', priority: 'high' },
      { task: 'Sharpen mower blades and service equipment', priority: 'medium' },
      { task: 'Apply lime if soil test shows low pH (if ground permits)', priority: 'low' },
      { task: 'Consider a soil test if you haven\'t done one in 2-3 years', priority: 'low' },
    ],
    // February
    [
      { task: 'Apply fertilizer (1 lb N/1,000 sq ft) before March 15', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Monitor soil temperatures weekly', priority: 'medium', toolLink: '/tools/soil-temperature' },
      { task: 'Plan pre-emergent timing (around forsythia bloom)', priority: 'medium' },
      { task: 'Continue staying off frozen areas', priority: 'low' },
    ],
    // March
    [
      { task: 'Apply pre-emergent when soil temps reach 53-55°F for several days', priority: 'high', toolLink: '/tools/herbicide-timing' },
      { task: 'Light raking to clear winter debris', priority: 'medium' },
      { task: 'Begin mowing when grass resumes growth at 2.5-3.5 inches', priority: 'medium' },
      { task: 'Check irrigation system', priority: 'low' },
    ],
    // April
    [
      { task: 'Mow at 2.5-3.5 inches regularly', priority: 'high' },
      { task: 'Spot-treat broadleaf weeds', priority: 'medium' },
      { task: 'Start irrigation if rainfall is below 1 inch/week', priority: 'medium' },
      { task: 'Do not fertilize after mid-April. Spring N promotes brown patch', priority: 'medium' },
    ],
    // May
    [
      { task: 'Continue regular mowing at 3-3.5 inches', priority: 'high' },
      { task: 'Apply grub preventer if history of problems', priority: 'medium' },
      { task: 'Last chance for herbicide applications before summer', priority: 'medium', toolLink: '/tools/herbicide-timing' },
      { task: 'Begin transitioning mowing height higher', priority: 'low' },
    ],
    // June
    [
      { task: 'Raise mowing height to 3.5-4 inches for summer', priority: 'high' },
      { task: 'Stop all nitrogen fertilization', priority: 'high' },
      { task: 'Water 1-1.25 inches/week, or allow dormancy (provide 0.5 inch every 3 weeks)', priority: 'high', toolLink: '/tools/watering-schedule' },
      { task: 'Watch for brown patch when daytime highs reach mid-80s and nights exceed 68°F', priority: 'medium' },
    ],
    // July
    [
      { task: 'Maintain 3.5-4 inch mowing height', priority: 'high' },
      { task: 'Water only in morning, avoid evening watering', priority: 'high' },
      { task: 'Watch for gray leaf spot (develops when nights exceed 70°F)', priority: 'medium' },
      { task: 'Minimize traffic on stressed turf', priority: 'medium' },
    ],
    // August
    [
      { task: 'Continue summer survival mode', priority: 'high' },
      { task: 'Plan fall aeration and overseeding', priority: 'high' },
      { task: 'Order fall seed (TTTF blend of 3+ varieties, 6 lbs/1,000 sq ft)', priority: 'medium' },
      { task: 'Watch soil temps as they begin to cool', priority: 'medium', toolLink: '/tools/soil-temperature' },
    ],
    // September
    [
      { task: 'Core aerate and overseed at 6 lbs/1,000 sq ft (TOP PRIORITY)', priority: 'high', toolLink: '/tools/seeding-calculator' },
      { task: 'Apply fall fertilizer (1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Lower mowing back to 2.5-3.5 inches', priority: 'medium' },
      { task: 'Water consistently for new seed germination', priority: 'high' },
    ],
    // October
    [
      { task: 'Continue watering new seed areas', priority: 'high' },
      { task: 'First mow new seedlings when they reach 3-3.5 inches, cut to 2-2.5 inches', priority: 'medium' },
      { task: 'Apply broadleaf herbicide for chickweed and henbit (avoid newly seeded areas)', priority: 'medium' },
      { task: 'Begin leaf removal', priority: 'medium' },
    ],
    // November
    [
      { task: 'Apply winterizer fertilizer (0.5-1 lb N)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Continue mowing at 3 inches until growth stops', priority: 'high' },
      { task: 'Remove all fallen leaves', priority: 'medium' },
      { task: 'Winterize irrigation system', priority: 'medium' },
    ],
    // December
    [
      { task: 'Lawn is dormant. Stay off frozen turf', priority: 'high' },
      { task: 'Keep leaves and debris cleared', priority: 'medium' },
      { task: 'Plan next year and enjoy the off-season', priority: 'low' },
      { task: 'Service equipment for spring', priority: 'low' },
    ],
  ],

  'Bermudagrass': [
    // January
    [
      { task: 'Bermuda is dormant and brown. This is normal', priority: 'high' },
      { task: 'Minimize foot traffic on frozen turf', priority: 'medium' },
      { task: 'Service mower, sharpen blades', priority: 'low' },
      { task: 'Plan spring scalping and pre-emergent timing', priority: 'low' },
    ],
    // February
    [
      { task: 'Apply pre-emergent before soil temperatures reach 55°F consistently', priority: 'high', toolLink: '/tools/herbicide-timing' },
      { task: 'Watch for first signs of green-up', priority: 'medium' },
      { task: 'Plan spring scalping date (only after last frost danger passes)', priority: 'medium' },
      { task: 'Monitor soil temperature weekly', priority: 'medium', toolLink: '/tools/soil-temperature' },
    ],
    // March
    [
      { task: 'Scalp lawn low after last frost danger has passed', priority: 'high' },
      { task: 'Wait for 50% green-up before fertilizing', priority: 'high' },
      { task: 'Check irrigation system for leaks', priority: 'medium' },
      { task: 'Watch soil temps approaching 65°F', priority: 'medium', toolLink: '/tools/soil-temperature' },
    ],
    // April
    [
      { task: 'First fertilizer once 50%+ green (1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Begin regular mowing at 1-2 inches (common) or 0.5-1.5 inches (hybrid)', priority: 'high' },
      { task: 'Treat broadleaf weeds with selective herbicide', priority: 'medium' },
      { task: 'Start irrigation if rainfall is insufficient', priority: 'medium' },
    ],
    // May
    [
      { task: 'Continue fertilizer every 4-6 weeks (1 lb N)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Mow every 5-7 days at proper height', priority: 'high' },
      { task: 'Apply preventive grub treatment if needed', priority: 'medium' },
      { task: 'Begin watching for armyworms', priority: 'medium' },
    ],
    // June
    [
      { task: 'Peak growing season. Mow every 3-5 days', priority: 'high' },
      { task: 'Core aerate if compacted (peak growth = fastest recovery)', priority: 'medium', toolLink: '/tools/aeration-calculator' },
      { task: 'Water 1-1.25 inches per week', priority: 'high', toolLink: '/tools/watering-schedule' },
      { task: 'Monitor for armyworms and sod webworms', priority: 'medium' },
    ],
    // July
    [
      { task: 'Continue peak season mowing (every 3-5 days)', priority: 'high' },
      { task: 'Apply fertilizer on schedule', priority: 'high' },
      { task: 'Water early morning, 1-1.25 inches/week', priority: 'high' },
      { task: 'Watch for bermudagrass mites and sod webworms', priority: 'medium' },
    ],
    // August
    [
      { task: 'Continue summer maintenance routine', priority: 'high' },
      { task: 'Last nitrogen application should be by mid-August', priority: 'high' },
      { task: 'Growth will begin slowing later this month', priority: 'medium' },
      { task: 'Plan fall pre-emergent and winterization', priority: 'low' },
    ],
    // September
    [
      { task: 'Apply potassium for winter hardiness (1 lb K2O/1,000 sq ft). Max 0.5 lb N', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
      { task: 'Reduce mowing frequency as growth slows', priority: 'medium' },
      { task: 'Apply fall pre-emergent for winter weeds', priority: 'medium', toolLink: '/tools/herbicide-timing' },
      { task: 'Continue irrigation until dormancy', priority: 'medium' },
    ],
    // October
    [
      { task: 'Bermuda starts going dormant as soil cools below 55°F', priority: 'high' },
      { task: 'Final mow before dormancy', priority: 'medium' },
      { task: 'Consider overseeding with ryegrass for winter color', priority: 'low' },
      { task: 'Do NOT fertilize with nitrogen', priority: 'high' },
    ],
    // November
    [
      { task: 'Bermuda is going dormant. Final cleanup', priority: 'medium' },
      { task: 'Winterize irrigation system', priority: 'medium' },
      { task: 'Remove leaves and debris', priority: 'medium' },
      { task: 'Do not fertilize', priority: 'high' },
    ],
    // December
    [
      { task: 'Bermuda is dormant and brown. Leave it alone', priority: 'high' },
      { task: 'Minimize foot traffic', priority: 'medium' },
      { task: 'Broadleaf herbicide for winter weeds (chickweed, henbit) is safe during dormancy', priority: 'low' },
      { task: 'Do not fertilize', priority: 'high' },
    ],
  ],
}

MONTHLY_TASKS['Fine Fescue'] = [
  // January
  [
    { task: 'Stay off frozen turf to prevent crown damage', priority: 'high' },
    { task: 'Sharpen mower blades and service equipment', priority: 'medium' },
    { task: 'Fine Fescue needs minimal inputs. Plan a light spring schedule', priority: 'low' },
    { task: 'Apply lime only if soil test confirmed low pH', priority: 'low' },
  ],
  // February
  [
    { task: 'Continue staying off frozen areas', priority: 'high' },
    { task: 'Watch for snow mold as snow melts', priority: 'medium' },
    { task: 'Plan pre-emergent timing', priority: 'medium' },
    { task: 'Monitor soil temperatures weekly', priority: 'low', toolLink: '/tools/soil-temperature' },
  ],
  // March
  [
    { task: 'Apply pre-emergent when soil temps reach 50-55°F for several consecutive days', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Light raking to clear winter debris', priority: 'medium' },
    { task: 'Begin mowing when grass resumes growth', priority: 'medium' },
    { task: 'Do not fertilize yet. Fine Fescue wakes up slowly', priority: 'low' },
  ],
  // April
  [
    { task: 'Mow at 2.5-3.5 inches', priority: 'high' },
    { task: 'Optional light fertilizer (0.25-0.5 lb N/1,000 sq ft)', priority: 'low', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Spot-treat broadleaf weeds carefully', priority: 'medium' },
    { task: 'Start irrigation only if rainfall is well below 1 inch/week', priority: 'low', toolLink: '/tools/watering-schedule' },
  ],
  // May
  [
    { task: 'Continue mowing at 2.5-3.5 inches', priority: 'high' },
    { task: 'Apply grub preventer if you have a history of problems', priority: 'medium' },
    { task: 'Last chance for broadleaf herbicide before summer stress', priority: 'medium', toolLink: '/tools/herbicide-timing' },
    { task: 'Keep nitrogen light. Fine Fescue needs only 0.5-2 lbs N total per year', priority: 'high' },
  ],
  // June
  [
    { task: 'Raise mowing height to 3.5-4 inches for summer', priority: 'high' },
    { task: 'Avoid nitrogen after mid-June', priority: 'high' },
    { task: 'Fine Fescue is drought-tolerant. Water only if severely stressed', priority: 'medium' },
    { task: 'Watch for red thread (if it appears, a light 0.25 lb N application helps)', priority: 'medium' },
  ],
  // July
  [
    { task: 'Maintain 3.5-4 inch mowing height. Do not mow when temps exceed 85°F', priority: 'high' },
    { task: 'Allow natural dormancy in drought rather than heavy watering', priority: 'medium' },
    { task: 'Do not fertilize', priority: 'high' },
    { task: 'Minimize traffic on stressed turf', priority: 'medium' },
  ],
  // August
  [
    { task: 'Continue summer survival mode. Avoid mowing in extreme heat', priority: 'high' },
    { task: 'Plan fall overseeding if lawn has thin patches', priority: 'medium' },
    { task: 'Order fall seed (mix of Fine Fescue species for best results)', priority: 'low' },
    { task: 'Watch soil temps as they begin to cool', priority: 'low', toolLink: '/tools/soil-temperature' },
  ],
  // September
  [
    { task: 'Overseed thin areas (Fine Fescue germinates in 7-14 days)', priority: 'high', toolLink: '/tools/seeding-calculator' },
    { task: 'Apply fall fertilizer (0.5-0.75 lb N/1,000 sq ft, keep annual total under 2 lbs)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Lower mowing back to 2.5-3.5 inches', priority: 'medium' },
    { task: 'Water consistently for new seed germination', priority: 'high' },
  ],
  // October
  [
    { task: 'Continue watering new seed areas', priority: 'high' },
    { task: 'First mow of new seedlings when they reach 3.5-4 inches', priority: 'medium' },
    { task: 'Begin leaf removal. Fine Fescue smothers easily under leaves', priority: 'medium' },
    { task: 'If you did NOT overseed, fall pre-emergent can target Poa annua', priority: 'low' },
  ],
  // November
  [
    { task: 'Optional light winterizer (0.25-0.5 lb N)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Continue mowing at 2.5-3 inches until growth stops', priority: 'high' },
    { task: 'Remove all fallen leaves', priority: 'medium' },
    { task: 'Winterize irrigation system', priority: 'medium' },
  ],
  // December
  [
    { task: 'Lawn is dormant. Stay off frozen turf', priority: 'high' },
    { task: 'Keep leaves and debris cleared', priority: 'medium' },
    { task: 'Plan next year. Fine Fescue thrives on neglect', priority: 'low' },
    { task: 'Service equipment for spring', priority: 'low' },
  ],
]

MONTHLY_TASKS['Perennial Ryegrass'] = [
  // January
  [
    { task: 'Minimize foot traffic on frozen turf', priority: 'high' },
    { task: 'Sharpen mower blades and service equipment', priority: 'medium' },
    { task: 'Order seed for fall. Ryegrass doesn\'t spread so plan to overseed thin areas', priority: 'medium' },
    { task: 'Apply lime if fall soil test showed low pH', priority: 'low' },
  ],
  // February
  [
    { task: 'Continue staying off frozen turf', priority: 'high' },
    { task: 'Watch for snow mold and pythium signs as snow melts', priority: 'medium' },
    { task: 'Plan your spring pre-emergent timing', priority: 'medium' },
    { task: 'Check soil temperature weekly as it warms', priority: 'low', toolLink: '/tools/soil-temperature' },
  ],
  // March
  [
    { task: 'Apply pre-emergent when soil temps reach 50-55°F for several consecutive days', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Light raking to remove winter debris', priority: 'medium' },
    { task: 'Check irrigation system for leaks', priority: 'medium' },
    { task: 'Begin mowing when grass is actively growing', priority: 'low' },
  ],
  // April
  [
    { task: 'Begin regular mowing at 2-3 inches', priority: 'high' },
    { task: 'Spot-treat broadleaf weeds', priority: 'medium' },
    { task: 'Start irrigation if rainfall drops below 1 inch/week', priority: 'medium', toolLink: '/tools/watering-schedule' },
    { task: 'Hold off on fertilizer until after spring growth flush slows (late April to May)', priority: 'medium' },
  ],
  // May
  [
    { task: 'Apply spring fertilizer (0.75-1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Apply grub preventer', priority: 'medium' },
    { task: 'Mow at 2.5-3 inches, never removing more than 1/3', priority: 'medium' },
    { task: 'Watch for gray leaf spot as humidity rises', priority: 'medium' },
  ],
  // June
  [
    { task: 'Raise mowing height to 2.5-3 inches for summer', priority: 'high' },
    { task: 'Water deeply 1-1.5 inches per week', priority: 'high', toolLink: '/tools/watering-schedule' },
    { task: 'Stop nitrogen fertilization for summer', priority: 'high' },
    { task: 'Watch for brown patch, pythium, and gray leaf spot', priority: 'medium' },
  ],
  // July
  [
    { task: 'Maintain 2.5-3 inch mowing height', priority: 'high' },
    { task: 'Water early morning only, avoid evening watering', priority: 'high' },
    { task: 'Ryegrass is less heat-tolerant than KBG. Expect some summer stress', priority: 'medium' },
    { task: 'Use iron for color if needed (not nitrogen)', priority: 'low' },
  ],
  // August
  [
    { task: 'Continue summer stress management', priority: 'high' },
    { task: 'Plan fall overseeding. This is critical since Ryegrass is a bunch-type grass', priority: 'high' },
    { task: 'Order seed (Ryegrass germinates fast, 5-7 days)', priority: 'medium' },
    { task: 'Watch soil temps as they begin cooling', priority: 'low', toolLink: '/tools/soil-temperature' },
  ],
  // September
  [
    { task: 'Core aerate and overseed (TOP PRIORITY since Ryegrass can\'t self-repair)', priority: 'high', toolLink: '/tools/seeding-calculator' },
    { task: 'Apply fall fertilizer (1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Lower mowing back to 2-3 inches', priority: 'medium' },
    { task: 'Water consistently for new seed germination', priority: 'high' },
  ],
  // October
  [
    { task: 'Continue watering overseeded areas', priority: 'high' },
    { task: 'First mow of new seedlings when they reach 3 inches', priority: 'medium' },
    { task: 'Begin leaf removal', priority: 'medium' },
    { task: 'If you did NOT overseed and have Poa annua problems, apply fall pre-emergent', priority: 'low' },
  ],
  // November
  [
    { task: 'Apply winterizer fertilizer (1 lb N/1,000 sq ft quick-release urea)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Continue mowing at 2-2.5 inches until growth stops', priority: 'high' },
    { task: 'Remove all fallen leaves', priority: 'medium' },
    { task: 'Winterize irrigation system', priority: 'medium' },
  ],
  // December
  [
    { task: 'Lawn is dormant. Minimize foot traffic', priority: 'high' },
    { task: 'Keep leaves and debris off the lawn', priority: 'medium' },
    { task: 'Review the year and plan improvements for spring', priority: 'low' },
    { task: 'Enjoy the off-season', priority: 'low' },
  ],
]

MONTHLY_TASKS['Zoysiagrass'] = [
  // January
  [
    { task: 'Zoysia is dormant and brown. This is normal', priority: 'high' },
    { task: 'Minimize foot traffic on frozen turf', priority: 'medium' },
    { task: 'Service mower, sharpen blades', priority: 'low' },
    { task: 'Plan spring pre-emergent timing', priority: 'low' },
  ],
  // February
  [
    { task: 'Apply pre-emergent before soil temperatures reach 55°F consistently', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Zoysia greens up later than Bermuda. Be patient', priority: 'medium' },
    { task: 'Monitor soil temperature weekly', priority: 'medium', toolLink: '/tools/soil-temperature' },
    { task: 'Large patch symptoms from fall infections may appear during green-up. Fall prevention is key', priority: 'medium' },
  ],
  // March
  [
    { task: 'Watch for first signs of green-up (Zoysia is slow to wake)', priority: 'medium' },
    { task: 'Assess thatch thickness. If over 0.5 inches, plan dethatching for May/June', priority: 'medium' },
    { task: 'Check irrigation system for leaks', priority: 'low' },
    { task: 'Do NOT fertilize until lawn is at least 50% green', priority: 'high' },
  ],
  // April
  [
    { task: 'Wait for 50%+ green-up before first fertilizer', priority: 'high' },
    { task: 'First fertilizer once green (0.5-1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Begin regular mowing at 1-2.5 inches', priority: 'high' },
    { task: 'Treat broadleaf weeds with selective herbicide', priority: 'medium' },
  ],
  // May
  [
    { task: 'Mow every 5-7 days at 1-2.5 inches', priority: 'high' },
    { task: 'Dethatch if thatch exceeds 0.5 inches (now that lawn is fully green)', priority: 'medium' },
    { task: 'Core aerate if compacted (late spring is ideal)', priority: 'medium', toolLink: '/tools/aeration-calculator' },
    { task: 'Start irrigation if rainfall is below 1 inch/week', priority: 'medium' },
  ],
  // June
  [
    { task: 'Mow every 5-7 days. Zoysia grows slower than Bermuda', priority: 'high' },
    { task: 'Water 1 inch per week', priority: 'high', toolLink: '/tools/watering-schedule' },
    { task: 'Second fertilizer if needed (0.5-1 lb N/1,000 sq ft)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Watch for billbugs and chinch bugs', priority: 'medium' },
  ],
  // July
  [
    { task: 'Maintain 1-2.5 inch mowing height every 5-7 days', priority: 'high' },
    { task: 'Water early morning, 1 inch per week', priority: 'high' },
    { task: 'Optional third fertilizer (0.5 lb N max). Keep annual total to 1-2 lbs N', priority: 'low', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Zoysia handles heat well but watch for drought stress', priority: 'medium' },
  ],
  // August
  [
    { task: 'Continue regular mowing routine', priority: 'high' },
    { task: 'Final nitrogen application by mid-August (0.5 lb N max)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Monitor for thatch buildup (common in Zoysia)', priority: 'medium' },
    { task: 'Growth will begin slowing later this month', priority: 'low' },
  ],
  // September
  [
    { task: 'Apply potassium for winter hardiness (1 lb K2O/1,000 sq ft). No nitrogen', priority: 'high' },
    { task: 'Reduce mowing frequency as growth slows', priority: 'medium' },
    { task: 'Apply fall pre-emergent for winter weeds', priority: 'medium', toolLink: '/tools/herbicide-timing' },
    { task: 'Watch for large patch disease as temps cool (preventive fungicide window)', priority: 'medium' },
  ],
  // October
  [
    { task: 'Zoysia begins going dormant as soil cools below 55°F', priority: 'high' },
    { task: 'Final mow before dormancy', priority: 'medium' },
    { task: 'Do NOT fertilize with nitrogen', priority: 'high' },
    { task: 'Continue irrigation until dormancy is complete', priority: 'medium' },
  ],
  // November
  [
    { task: 'Zoysia is going dormant. Final cleanup', priority: 'medium' },
    { task: 'Winterize irrigation system', priority: 'medium' },
    { task: 'Remove leaves and debris', priority: 'medium' },
    { task: 'Do not fertilize or apply herbicide', priority: 'high' },
  ],
  // December
  [
    { task: 'Zoysia is dormant and brown. Leave it alone', priority: 'high' },
    { task: 'Minimize foot traffic', priority: 'medium' },
    { task: 'Plan next year and service equipment', priority: 'low' },
    { task: 'Do not fertilize or herbicide', priority: 'high' },
  ],
]

MONTHLY_TASKS['St. Augustinegrass'] = [
  // January
  [
    { task: 'St. Augustine is dormant or semi-dormant depending on zone', priority: 'high' },
    { task: 'Minimize foot traffic on stressed turf', priority: 'medium' },
    { task: 'Service mower, sharpen blades', priority: 'low' },
    { task: 'Plan spring fertilizer and pest control schedule', priority: 'low' },
  ],
  // February
  [
    { task: 'Apply pre-emergent before soil temperatures reach 55°F consistently', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Watch for early green-up in warmer zones', priority: 'medium' },
    { task: 'Monitor soil temperature weekly', priority: 'medium', toolLink: '/tools/soil-temperature' },
    { task: 'Watch for large patch disease in cool, wet conditions', priority: 'medium' },
  ],
  // March
  [
    { task: 'Begin mowing at 3-4 inches as green-up starts', priority: 'high' },
    { task: 'Wait for active growth before fertilizing', priority: 'high' },
    { task: 'Check irrigation system for leaks', priority: 'medium' },
    { task: 'Watch for take-all root rot (TARR) and large patch during green-up', priority: 'medium' },
  ],
  // April
  [
    { task: 'First fertilizer once actively growing (0.7 lb soluble N or 1 lb slow-release)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Mow at 3.5-4 inches following the 1/3 rule', priority: 'high' },
    { task: 'Treat broadleaf weeds with selective herbicide (avoid atrazine above 85°F)', priority: 'medium' },
    { task: 'Begin scouting for chinch bugs', priority: 'medium' },
  ],
  // May
  [
    { task: 'Continue mowing at 3.5-4 inches following the 1/3 rule', priority: 'high' },
    { task: 'Apply second fertilizer (0.5-1 lb N/1,000 sq ft slow-release)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Core aerate if compacted (skip if pre-emergent was applied recently)', priority: 'medium', toolLink: '/tools/aeration-calculator' },
    { task: 'Chinch bugs become active. Inspect sunny, dry areas', priority: 'high' },
  ],
  // June
  [
    { task: 'Peak growing season. Mow following the 1/3 rule at 3.5-4 inches', priority: 'high' },
    { task: 'Water 3/4-1 inch per week. St. Augustine needs consistent moisture', priority: 'high', toolLink: '/tools/watering-schedule' },
    { task: 'Chinch bug damage peaks. Treat immediately if found', priority: 'high' },
    { task: 'Watch for gray leaf spot. Avoid excess nitrogen', priority: 'medium' },
  ],
  // July
  [
    { task: 'Continue mowing at 3.5-4 inches following the 1/3 rule', priority: 'high' },
    { task: 'Water early morning, 3/4-1 inch/week', priority: 'high' },
    { task: 'Continue monitoring for chinch bugs in hot, sunny areas', priority: 'high' },
    { task: 'Monitor for mole crickets (mid-July is key treatment window for nymphs)', priority: 'medium' },
  ],
  // August
  [
    { task: 'Continue summer maintenance routine', priority: 'high' },
    { task: 'Last nitrogen application by mid-August in northern zones', priority: 'high' },
    { task: 'Gray leaf spot risk is high. Avoid excess nitrogen', priority: 'medium' },
    { task: 'Growth will begin slowing toward end of month', priority: 'low' },
  ],
  // September
  [
    { task: 'Final fertilizer: use high-K product (e.g., 15-0-15) while grass is still growing', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Reduce mowing frequency as growth slows', priority: 'medium' },
    { task: 'Apply fall pre-emergent for winter weeds', priority: 'medium', toolLink: '/tools/herbicide-timing' },
    { task: 'Continue irrigation until dormancy', priority: 'medium' },
  ],
  // October
  [
    { task: 'St. Augustine begins going dormant in northern zones', priority: 'high' },
    { task: 'Final mow before dormancy', priority: 'medium' },
    { task: 'Do NOT fertilize with nitrogen', priority: 'high' },
    { task: 'Do not plug or sod this late. Wait until next spring', priority: 'medium' },
  ],
  // November
  [
    { task: 'St. Augustine is going dormant. Final cleanup', priority: 'medium' },
    { task: 'Winterize irrigation system', priority: 'medium' },
    { task: 'Remove leaves and debris', priority: 'medium' },
    { task: 'Atrazine or simazine can be applied for winter annual weeds', priority: 'low' },
  ],
  // December
  [
    { task: 'St. Augustine is dormant. Leave it alone', priority: 'high' },
    { task: 'Minimize foot traffic', priority: 'medium' },
    { task: 'Plan next year and service equipment', priority: 'low' },
    { task: 'Do not fertilize. Winter broadleaf herbicides are OK if needed', priority: 'medium' },
  ],
]

MONTHLY_TASKS['Centipede'] = [
  // January
  [
    { task: 'Centipede is dormant and brown. This is normal', priority: 'high' },
    { task: 'Minimize foot traffic', priority: 'medium' },
    { task: 'Service mower, sharpen blades', priority: 'low' },
    { task: 'Do NOT plan heavy fertilization. Centipede thrives on neglect', priority: 'medium' },
  ],
  // February
  [
    { task: 'Apply pre-emergent before soil temperatures reach 55°F (late Feb to mid-March)', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Watch for early green-up', priority: 'medium' },
    { task: 'Monitor soil temperature weekly', priority: 'medium', toolLink: '/tools/soil-temperature' },
    { task: 'Do not apply lime unless soil test specifically says to. Centipede prefers acidic soil (pH 5.0-6.0)', priority: 'high' },
  ],
  // March
  [
    { task: 'Watch for green-up. Centipede is slow to wake', priority: 'medium' },
    { task: 'Do NOT fertilize until lawn is fully green (typically mid-May)', priority: 'high' },
    { task: 'Check irrigation system', priority: 'low' },
    { task: 'Light raking to remove debris', priority: 'low' },
  ],
  // April
  [
    { task: 'Begin mowing at 1.5-2.5 inches once actively growing', priority: 'high' },
    { task: 'Do NOT fertilize yet. Wait for full green-up (typically May)', priority: 'high' },
    { task: 'Too much nitrogen causes Centipede decline. Less is more', priority: 'medium' },
    { task: 'Treat broadleaf weeds carefully. Centipede is sensitive to some herbicides', priority: 'medium' },
  ],
  // May
  [
    { task: 'First fertilizer once fully green (0.5 lb N/1,000 sq ft max)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Mow at 1.5-2.5 inches every 7-14 days', priority: 'high' },
    { task: 'Dethatch if thatch exceeds 0.5 inches', priority: 'medium' },
    { task: 'Start irrigation if rainfall is below 1 inch/week', priority: 'medium' },
  ],
  // June
  [
    { task: 'Mow every 7-14 days at 1.5-2.5 inches', priority: 'high' },
    { task: 'Water 1 inch per week', priority: 'high', toolLink: '/tools/watering-schedule' },
    { task: 'Optional second fertilizer (0.5 lb N max) using high-K product. Annual total: 1-2 lbs N', priority: 'low', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Monitor for ground pearls and nematodes', priority: 'medium' },
  ],
  // July
  [
    { task: 'Continue mowing every 7-14 days at 1.5-2.5 inches', priority: 'high' },
    { task: 'Water early morning, 1 inch per week', priority: 'high' },
    { task: 'Apply iron sulfate for color if desired (not nitrogen)', priority: 'low' },
    { task: 'Watch for large patch disease and spittlebugs', priority: 'medium' },
  ],
  // August
  [
    { task: 'Continue minimal summer care', priority: 'high' },
    { task: 'No nitrogen after mid-August at the latest', priority: 'high' },
    { task: 'Growth begins slowing toward end of month', priority: 'medium' },
    { task: 'Apply iron sulfate for color if desired (not nitrogen)', priority: 'low' },
  ],
  // September
  [
    { task: 'Apply potassium for winter hardiness (1 lb K2O/1,000 sq ft using 0-0-50 or 0-0-60)', priority: 'medium' },
    { task: 'Reduce mowing frequency as growth slows', priority: 'medium' },
    { task: 'Apply fall pre-emergent for winter weeds by mid-September', priority: 'medium', toolLink: '/tools/herbicide-timing' },
    { task: 'Continue irrigation until dormancy', priority: 'medium' },
  ],
  // October
  [
    { task: 'Centipede begins going dormant as soil cools', priority: 'high' },
    { task: 'Final mow before dormancy', priority: 'medium' },
    { task: 'Do NOT fertilize with nitrogen', priority: 'high' },
    { task: 'Do NOT apply lime (Centipede needs acidic soil)', priority: 'high' },
  ],
  // November
  [
    { task: 'Centipede is going dormant. Final cleanup', priority: 'medium' },
    { task: 'Winterize irrigation system', priority: 'medium' },
    { task: 'Remove leaves and debris', priority: 'medium' },
    { task: 'Atrazine or simazine can be applied for winter annual weeds', priority: 'low' },
  ],
  // December
  [
    { task: 'Centipede is dormant and brown. Leave it alone', priority: 'high' },
    { task: 'Minimize foot traffic', priority: 'medium' },
    { task: 'Plan next year. Remember: minimal fertilizer is key', priority: 'low' },
    { task: 'Do not fertilize. Winter broadleaf herbicides are OK if needed', priority: 'medium' },
  ],
]

MONTHLY_TASKS['Bahiagrass'] = [
  // January
  [
    { task: 'Bahia is dormant and brown. This is normal', priority: 'high' },
    { task: 'Minimize foot traffic on frozen turf', priority: 'medium' },
    { task: 'Service mower, sharpen blades', priority: 'low' },
    { task: 'Plan spring fertilizer schedule', priority: 'low' },
  ],
  // February
  [
    { task: 'Apply pre-emergent by Feb 1 (South FL), Feb 15 (Central FL), or March 1 (North FL)', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Watch for first signs of green-up', priority: 'medium' },
    { task: 'Monitor soil temperature weekly', priority: 'medium', toolLink: '/tools/soil-temperature' },
    { task: 'Plan for mole cricket control (main pest for Bahia)', priority: 'medium' },
  ],
  // March
  [
    { task: 'Watch for green-up as soil warms', priority: 'medium' },
    { task: 'Do NOT fertilize until actively growing', priority: 'high' },
    { task: 'Check irrigation system for leaks', priority: 'low' },
    { task: 'Begin mowing at 3-4 inches when growth starts', priority: 'medium' },
  ],
  // April
  [
    { task: 'First fertilizer once actively growing (0.5-1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Begin regular mowing at 3-4 inches', priority: 'high' },
    { task: 'Treat broadleaf weeds with selective herbicide', priority: 'medium' },
    { task: 'Scout for mole cricket activity using soap flush method', priority: 'medium' },
  ],
  // May
  [
    { task: 'Mow every 7-14 days at 3-4 inches', priority: 'high' },
    { task: 'Second fertilizer application (0.5-1 lb N)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Seed heads will appear and look unsightly. This is normal for Bahia', priority: 'medium' },
    { task: 'Mow regularly to control seed heads', priority: 'medium' },
  ],
  // June
  [
    { task: 'Peak growing season. Mow every 7-10 days at 3-4 inches', priority: 'high' },
    { task: 'Bahia is very drought-tolerant. Water only if severely stressed', priority: 'medium', toolLink: '/tools/watering-schedule' },
    { task: 'Apply mole cricket bait targeting newly hatched nymphs', priority: 'high' },
    { task: 'Optional fertilizer (0.5 lb N) if lawn looks pale', priority: 'low', toolLink: '/tools/fertilizer-calculator' },
  ],
  // July
  [
    { task: 'Continue mowing every 7-10 days at 3-4 inches', priority: 'high' },
    { task: 'Water early morning only if needed', priority: 'medium' },
    { task: 'Seed heads continue. Frequent mowing helps control them', priority: 'medium' },
    { task: 'Keep total annual N to 1-2 lbs per 1,000 sq ft', priority: 'high' },
  ],
  // August
  [
    { task: 'Continue summer maintenance routine', priority: 'high' },
    { task: 'Watch for fall armyworm damage (rapidly expanding brown patches)', priority: 'high' },
    { task: 'Growth will begin slowing later this month', priority: 'medium' },
    { task: 'No more nitrogen after this month', priority: 'high' },
  ],
  // September
  [
    { task: 'If making a final fertilizer application, use a high-K product (e.g., 15-0-15)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Reduce mowing frequency as growth slows', priority: 'medium' },
    { task: 'Apply fall pre-emergent for winter weeds', priority: 'medium', toolLink: '/tools/herbicide-timing' },
    { task: 'Continue irrigation until dormancy', priority: 'medium' },
  ],
  // October
  [
    { task: 'Bahia begins going dormant as temperatures drop', priority: 'high' },
    { task: 'Final mow before dormancy', priority: 'medium' },
    { task: 'Do NOT fertilize with nitrogen', priority: 'high' },
    { task: 'Do not fertilize or apply herbicide', priority: 'medium' },
  ],
  // November
  [
    { task: 'Bahia is going dormant. Final cleanup', priority: 'medium' },
    { task: 'Winterize irrigation system', priority: 'medium' },
    { task: 'Remove leaves and debris', priority: 'medium' },
    { task: 'Do not fertilize', priority: 'high' },
  ],
  // December
  [
    { task: 'Bahia is dormant and brown. Leave it alone', priority: 'high' },
    { task: 'Minimize foot traffic', priority: 'medium' },
    { task: 'Plan next year and service equipment', priority: 'low' },
    { task: 'Do not fertilize', priority: 'high' },
  ],
]

MONTHLY_TASKS['Buffalograss'] = [
  // January
  [
    { task: 'Buffalograss is dormant. No action needed', priority: 'low' },
    { task: 'Do not water, fertilize, or mow', priority: 'high' },
    { task: 'Plan for weed control if Bermuda invasion is a concern', priority: 'low' },
    { task: 'Service equipment for spring', priority: 'low' },
  ],
  // February
  [
    { task: 'Buffalograss is dormant. No action needed', priority: 'low' },
    { task: 'Do not water, fertilize, or mow', priority: 'high' },
    { task: 'Monitor for early weed emergence', priority: 'low' },
    { task: 'Plan pre-emergent for April if crabgrass was a problem', priority: 'low' },
  ],
  // March
  [
    { task: 'Buffalograss is still dormant. No action needed', priority: 'low' },
    { task: 'Spot-treat broadleaf weeds while dormant (apply when temps reach 50°F+)', priority: 'medium' },
    { task: 'Do not water, fertilize, or mow', priority: 'high' },
    { task: 'Buffalo is one of the last grasses to green up. Be patient', priority: 'medium' },
  ],
  // April
  [
    { task: 'Apply pre-emergent for crabgrass (April 1-15 is the recommended window)', priority: 'medium', toolLink: '/tools/herbicide-timing' },
    { task: 'Watch for green-up (Buffalo is one of the last to wake up)', priority: 'medium' },
    { task: 'Do NOT fertilize until fully green', priority: 'high' },
    { task: 'Monitor for Bermuda invasion', priority: 'medium' },
  ],
  // May
  [
    { task: 'Buffalo enters active growth', priority: 'medium' },
    { task: 'Begin mowing if desired (3-4 inches, or leave natural)', priority: 'low' },
    { task: 'Do not fertilize yet. Wait until June when fully established in growth', priority: 'medium' },
    { task: 'Hand-pull any invading Bermuda grass', priority: 'medium' },
  ],
  // June
  [
    { task: 'Apply fertilizer (0.5-1 lb N/1,000 sq ft). This may be the only application needed', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Mow every 2-3 weeks at 3-4 inches if maintaining mowed look', priority: 'low' },
    { task: 'Water deeply every 2 weeks during hot, dry weather', priority: 'low' },
    { task: 'Monitor for competing grasses', priority: 'medium' },
  ],
  // July
  [
    { task: 'Peak growing season for Buffalo', priority: 'medium' },
    { task: 'Optional second fertilizer (0.5-1 lb N). Annual total should not exceed 2 lbs N', priority: 'low', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Mow if needed (every 2-3 weeks at 3-4 inches)', priority: 'low' },
    { task: 'Supplemental watering every 2 weeks supports stolon growth', priority: 'low' },
  ],
  // August
  [
    { task: 'Continue minimal summer care', priority: 'low' },
    { task: 'Growth begins slowing toward end of month', priority: 'medium' },
    { task: 'Final mow of the season if desired', priority: 'low' },
    { task: 'Do not fertilize after this month', priority: 'high' },
  ],
  // September
  [
    { task: 'Growth is slowing. Reduce any mowing', priority: 'low' },
    { task: 'Buffalo begins transitioning to dormancy', priority: 'medium' },
    { task: 'Do not fertilize or overseed', priority: 'high' },
    { task: 'Remove debris from lawn', priority: 'low' },
  ],
  // October
  [
    { task: 'Apply broadleaf herbicide on a day above 50°F if weeds are present', priority: 'high' },
    { task: 'This is the most effective weed treatment window for buffalograss', priority: 'medium' },
    { task: 'Do not fertilize', priority: 'high' },
    { task: 'Remove fallen leaves', priority: 'low' },
  ],
  // November
  [
    { task: 'Buffalograss is dormant. Broadleaf herbicide still effective while dormant', priority: 'medium' },
    { task: 'Do not water, fertilize, or mow', priority: 'high' },
    { task: 'Remove fallen leaves and debris', priority: 'low' },
    { task: 'Plan for next spring if needed', priority: 'low' },
  ],
  // December
  [
    { task: 'Buffalograss is dormant. No action needed', priority: 'low' },
    { task: 'Do not water, fertilize, or mow', priority: 'high' },
    { task: 'Plan for next spring if needed', priority: 'low' },
    { task: 'Service equipment', priority: 'low' },
  ],
]

// Warm-season, coastal California focus (the guide's home turf). Calendar
// grounded in data/guides/kikuyugrass.ts: ~2 lbs N/1,000 sq ft/year during
// active growth (UC IPM), mowing every 4-7 days at peak, runner containment
// and thatch management as recurring work, partial winter dormancy with frost.
MONTHLY_TASKS['Kikuyugrass'] = [
  // January
  [
    { task: 'Growth is slow or paused. Mow only if still actively growing', priority: 'medium' },
    { task: 'Do not fertilize to force winter color', priority: 'high' },
    { task: 'Frost-browned patches are normal. Wait for spring, do not reseed yet', priority: 'medium' },
    { task: 'Service the mower. Kikuyu demands a fast cutting cadence all season', priority: 'low' },
  ],
  // February
  [
    { task: 'Apply pre-emergent for crabgrass when soil temps reach 50-55°F (early in mild coastal areas)', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Spot-treat winter broadleaf weeds while the canopy is thin', priority: 'medium' },
    { task: 'Still no nitrogen. Wait for clear, sustained green-up', priority: 'high' },
    { task: 'Check irrigation heads and edging barriers before the growth surge', priority: 'low' },
  ],
  // March
  [
    { task: 'Begin regular mowing at 1-2 inches as green-up starts', priority: 'high' },
    { task: 'Walk the edges and cut back any runners escaping into beds or paths', priority: 'medium' },
    { task: 'Soil test if it has been 2-3 years (pH, salinity, nutrients)', priority: 'medium' },
    { task: 'Hold fertilizer until the lawn is actively growing and mowed once or twice', priority: 'medium' },
  ],
  // April
  [
    { task: 'Apply spring fertilizer once actively growing (0.5-0.75 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Tighten the mowing cadence. Never remove more than 1/3 of the blade', priority: 'high' },
    { task: 'Edge and contain runners monthly from now through fall', priority: 'medium' },
    { task: 'Start deep, infrequent watering as rainfall tapers off', priority: 'medium', toolLink: '/tools/watering-schedule' },
  ],
  // May
  [
    { task: 'Core aerate and dethatch while growth is rapid (recovery is fastest now)', priority: 'high', toolLink: '/tools/aeration-calculator' },
    { task: 'Mow every 5-7 days. Missed cuts turn Kikuyu stemmy and scalp-prone', priority: 'high' },
    { task: 'Check thatch depth. Over 1/2 inch means schedule removal', priority: 'medium' },
    { task: 'Top-dress thinly with screened compost after aerating if soil is poor', priority: 'low' },
  ],
  // June
  [
    { task: 'Mow every 4-7 days at 1-2 inches through peak growth', priority: 'high' },
    { task: 'Apply a light summer feeding if color fades (0.5 lb N/1,000 sq ft)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Water deeply and infrequently, not daily', priority: 'medium', toolLink: '/tools/watering-schedule' },
    { task: 'Cut escaped runners at beds, fences, and hardscape edges', priority: 'medium' },
  ],
  // July
  [
    { task: 'Keep the fast mowing cadence. This is the highest-growth month', priority: 'high' },
    { task: 'Limit fertilizer during peak heat (UC IPM guidance)', priority: 'high' },
    { task: 'Water early morning, deep and infrequent', priority: 'medium', toolLink: '/tools/watering-schedule' },
    { task: 'Mow clean lawns before Kikuyu areas, then clean the deck (it spreads by fragments)', priority: 'low' },
  ],
  // August
  [
    { task: 'Continue mowing every 4-7 days', priority: 'high' },
    { task: 'Re-check thatch. Late summer is the last good removal window', priority: 'medium' },
    { task: 'Keep nitrogen restrained until the heat breaks', priority: 'medium' },
    { task: 'Inspect boundaries. Runners root easily in moist soil beyond the lawn', priority: 'medium' },
  ],
  // September
  [
    { task: 'Apply early-fall fertilizer (0.5-0.75 lb N/1,000 sq ft) to finish near 2 lbs N for the year', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Last call for core aeration while growth is still strong', priority: 'medium', toolLink: '/tools/aeration-calculator' },
    { task: 'Maintain the mowing cadence as growth begins easing', priority: 'medium' },
    { task: 'Reduce irrigation as days shorten', priority: 'low', toolLink: '/tools/watering-schedule' },
  ],
  // October
  [
    { task: 'Apply fall pre-emergent for winter annual weeds (Poa annua)', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Mowing slows. Cut as needed, keep the 1/3 rule', priority: 'medium' },
    { task: 'Final edging pass. Clean boundaries before growth stops', priority: 'medium' },
    { task: 'Dial irrigation back further', priority: 'low' },
  ],
  // November
  [
    { task: 'Do not fertilize to force color as weather cools', priority: 'high' },
    { task: 'Mow occasionally if still growing (coastal lawns may stay partly green)', priority: 'medium' },
    { task: 'Remove fallen leaves so they do not mat over the runners', priority: 'medium' },
    { task: "Note which zones brown first. It guides next year's watering and care", priority: 'low' },
  ],
  // December
  [
    { task: 'Partial dormancy and frost browning are normal. No action needed', priority: 'medium' },
    { task: 'Do not fertilize', priority: 'high' },
    { task: 'Minimize traffic on frosted turf', priority: 'medium' },
    { task: 'Plan spring renovation and service equipment', priority: 'low' },
  ],
]

// Temporary winter overseed over a dormant Bermudagrass base (the guide's
// primary use case, data/guides/annual-ryegrass.ts). The calendar follows the
// overseed lifecycle: fall seeding, winter mowing, spring transition back to
// Bermuda, and summer care of the Bermuda base.
MONTHLY_TASKS['Annual Ryegrass'] = [
  // January
  [
    { task: 'Mow the winter rye at 1.5-2.5 inches whenever it needs it', priority: 'high' },
    { task: 'Water during extended dry spells (winter rain usually covers it)', priority: 'medium', toolLink: '/tools/watering-schedule' },
    { task: 'Apply a light feeding if color fades (0.5 lb N/1,000 sq ft max)', priority: 'low', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Stay off the lawn when frosted', priority: 'medium' },
  ],
  // February
  [
    { task: 'Keep mowing. Annual rye grows through mild winter weather', priority: 'high' },
    { task: 'Spot-treat winter broadleaf weeds', priority: 'medium' },
    { task: 'Plan the spring transition. The rye must be pushed out for Bermuda to return', priority: 'medium' },
    { task: 'No more nitrogen from here on. It only delays the transition', priority: 'medium' },
  ],
  // March
  [
    { task: 'Begin lowering the mowing height gradually to stress the rye', priority: 'high' },
    { task: 'Reduce watering. The goal is now favoring Bermuda, not the rye', priority: 'medium' },
    { task: 'Seed heads appearing are normal. The rye is finishing its life cycle', priority: 'low' },
    { task: 'Do not fertilize until the Bermuda base is greening', priority: 'medium' },
  ],
  // April
  [
    { task: 'Transition month: mow short and hold back water so Bermuda takes over', priority: 'high' },
    { task: 'Watch for Bermuda green-up underneath the fading rye', priority: 'medium' },
    { task: 'Do not feed the rye. Wait for the Bermuda', priority: 'medium' },
    { task: 'Check soil temps. Bermuda wakes when soil passes 65°F', priority: 'low', toolLink: '/tools/soil-temperature' },
  ],
  // May
  [
    { task: 'Fertilize the Bermuda base once fully green (1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Rye fading out in the heat is normal and expected', priority: 'medium' },
    { task: 'Mow the Bermuda low and regularly (1-2 inches)', priority: 'medium' },
    { task: 'Water deeply, about 1 inch per week', priority: 'medium', toolLink: '/tools/watering-schedule' },
  ],
  // June
  [
    { task: 'The rye is gone. Summer is about building a dense Bermuda base', priority: 'high' },
    { task: 'Fertilize Bermuda monthly (0.5-1 lb N/1,000 sq ft)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Mow every 5-7 days', priority: 'medium' },
    { task: 'A thick Bermuda lawn now means a better overseed this fall', priority: 'low' },
  ],
  // July
  [
    { task: 'Continue regular Bermuda care: mow, water deeply, feed monthly', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Treat weeds now. Herbicide options narrow once you plan to seed', priority: 'medium' },
    { task: 'Water early morning, 1-1.25 inches per week in heat', priority: 'medium', toolLink: '/tools/watering-schedule' },
    { task: 'Confirm irrigation covers the whole lawn evenly (seeding depends on it)', priority: 'low' },
  ],
  // August
  [
    { task: 'Skip fall pre-emergent on any area you plan to overseed. It will block the rye seed', priority: 'high' },
    { task: 'Order Annual Ryegrass seed (5-10 lbs/1,000 sq ft)', priority: 'medium', toolLink: '/tools/seeding-calculator' },
    { task: 'Last Bermuda feeding of the year by late August', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Keep mowing the Bermuda regularly', priority: 'low' },
  ],
  // September
  [
    { task: 'Watch soil temps. Seed when they fall to about 70°F', priority: 'high', toolLink: '/tools/soil-temperature' },
    { task: 'Late month: mow the Bermuda short and bag clippings to prep the seedbed', priority: 'medium' },
    { task: 'Test-run the irrigation. New seed needs light, even coverage', priority: 'medium' },
    { task: 'Do not fertilize the Bermuda. It is heading into dormancy', priority: 'medium' },
  ],
  // October
  [
    { task: 'Overseed: 5-10 lbs/1,000 sq ft into the scalped Bermuda', priority: 'high', toolLink: '/tools/seeding-calculator' },
    { task: 'Water lightly 1-2x daily until germination (5-10 days), then taper', priority: 'high', toolLink: '/tools/watering-schedule' },
    { task: 'First mow at 2-2.5 inches once the rye stands up to it', priority: 'medium' },
    { task: 'A light starter fertilizer helps the seedlings fill in', priority: 'low', toolLink: '/tools/fertilizer-calculator' },
  ],
  // November
  [
    { task: 'Mow the new rye regularly at 1.5-2.5 inches', priority: 'high' },
    { task: 'Shift to deeper, less frequent watering as the stand matures', priority: 'medium', toolLink: '/tools/watering-schedule' },
    { task: 'Light feeding once established (0.5 lb N/1,000 sq ft)', priority: 'medium', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Keep fallen leaves off the young stand', priority: 'medium' },
  ],
  // December
  [
    { task: 'Keep mowing as needed. Winter growth continues in mild weather', priority: 'high' },
    { task: 'Water during dry spells', priority: 'medium' },
    { task: 'Minimal nitrogen. Save any feeding for a mid-winter color fade', priority: 'low' },
    { task: "Enjoy the green lawn while the neighbors' Bermuda sleeps", priority: 'low' },
  ],
]

// KBG + perennial rye + fescue blends (data/guides/mixed-cool-season-blend.ts):
// a standard northern cool-season calendar — 2-3 lbs N/1,000 sq ft/year, mow at
// 3-3.5 inches, September aeration + blend-matched overseeding as the anchor.
MONTHLY_TASKS['Mixed Cool-Season Blend'] = [
  // January
  [
    { task: 'Minimize foot traffic on frozen turf', priority: 'high' },
    { task: 'Sharpen mower blades and service equipment', priority: 'medium' },
    { task: 'Order a blend-matched seed mix and fertilizer for the year', priority: 'medium' },
    { task: 'Apply lime if a fall soil test showed low pH (if ground permits)', priority: 'low' },
  ],
  // February
  [
    { task: 'Continue staying off frozen turf', priority: 'high' },
    { task: 'Watch for snow mold as snow melts (rye and fescue patches show it first)', priority: 'medium' },
    { task: 'Plan your spring pre-emergent timing', priority: 'medium' },
    { task: 'Check soil temperature weekly as it warms', priority: 'low', toolLink: '/tools/soil-temperature' },
  ],
  // March
  [
    { task: 'Apply pre-emergent when soil temps reach 50-55°F for several consecutive days', priority: 'high', toolLink: '/tools/herbicide-timing' },
    { task: 'Light raking to remove winter debris', priority: 'medium' },
    { task: 'Check irrigation system for leaks', priority: 'medium' },
    { task: 'Begin mowing when the grass is actively growing', priority: 'low' },
  ],
  // April
  [
    { task: 'Begin regular mowing at 3-3.5 inches', priority: 'high' },
    { task: 'Spot-treat broadleaf weeds', priority: 'medium' },
    { task: 'Start irrigation if rainfall drops below 1 inch/week', priority: 'medium', toolLink: '/tools/watering-schedule' },
    { task: 'Hold fertilizer until the spring growth flush slows (late April to May)', priority: 'medium' },
  ],
  // May
  [
    { task: 'Apply spring fertilizer after the flush slows (0.75-1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Apply grub preventer (chlorantraniliprole products)', priority: 'medium' },
    { task: 'Mow at 3-3.5 inches, never removing more than 1/3', priority: 'medium' },
    { task: 'Note which grasses dominate where. Some patchiness is normal in a blend', priority: 'low' },
  ],
  // June
  [
    { task: 'Raise the mowing height to 3.5 inches for summer', priority: 'high' },
    { task: 'Water deeply, 1-1.5 inches per week', priority: 'high', toolLink: '/tools/watering-schedule' },
    { task: 'Stop nitrogen fertilization for summer', priority: 'high' },
    { task: 'Watch for brown patch and dollar spot (the rye and fescue are most susceptible)', priority: 'medium' },
  ],
  // July
  [
    { task: 'Maintain the 3.5 inch mowing height', priority: 'high' },
    { task: 'Water early morning only, avoid evening watering', priority: 'high' },
    { task: 'Use iron for color if needed (not nitrogen)', priority: 'medium' },
    { task: 'Fine-fescue areas may brown in heat. Do not overwater trying to force them green', priority: 'medium' },
  ],
  // August
  [
    { task: 'Continue summer survival care', priority: 'high' },
    { task: 'Plan fall aeration and overseeding. It is how a blend stays even', priority: 'medium' },
    { task: 'Watch soil temps as they begin cooling', priority: 'medium', toolLink: '/tools/soil-temperature' },
    { task: 'Order seed that matches your existing blend (check the label percentages)', priority: 'low' },
  ],
  // September
  [
    { task: 'Core aerate and overseed with a matching blend (MOST IMPORTANT month)', priority: 'high', toolLink: '/tools/aeration-calculator' },
    { task: 'Apply fall fertilizer at seeding (1 lb N/1,000 sq ft)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Lower mowing back to 3 inches', priority: 'medium' },
    { task: 'Keep overseeded areas consistently moist', priority: 'medium', toolLink: '/tools/watering-schedule' },
  ],
  // October
  [
    { task: 'Continue watering overseeded areas', priority: 'high' },
    { task: 'First mow of new seedlings when they reach 3.5 inches', priority: 'medium' },
    { task: 'Begin leaf removal', priority: 'medium' },
    { task: 'Fall is the best broadleaf weed treatment window', priority: 'low' },
  ],
  // November
  [
    { task: 'Apply winterizer fertilizer (1 lb N/1,000 sq ft quick-release)', priority: 'high', toolLink: '/tools/fertilizer-calculator' },
    { task: 'Continue mowing at 3 inches until growth stops', priority: 'high' },
    { task: 'Remove all fallen leaves', priority: 'medium' },
    { task: 'Winterize the irrigation system', priority: 'medium' },
  ],
  // December
  [
    { task: 'Lawn is dormant. Minimize foot traffic', priority: 'high' },
    { task: 'Keep leaves and debris off the lawn', priority: 'medium' },
    { task: "Review the year: note thin or patchy zones for next fall's overseed", priority: 'low' },
    { task: 'Enjoy the off-season', priority: 'low' },
  ],
]

MONTHLY_TASKS['Carpetgrass'] = [
  // January
  [
    { task: 'Keep dormant or slow-growing turf free of leaves and debris', priority: 'medium' },
    { task: 'Avoid traffic on wet soil', priority: 'high' },
    { task: 'Do not apply nitrogen while the grass is not actively growing', priority: 'high' },
  ],
  // February
  [
    { task: 'Service the mower and sharpen the blade before spring growth', priority: 'low' },
    { task: 'Check low areas for runoff, compaction, or standing water', priority: 'medium' },
    { task: 'Plan a soil test before adding lime or fertilizer', priority: 'medium' },
  ],
  // March
  [
    { task: 'Watch for green-up as warm-season growth resumes', priority: 'medium' },
    { task: 'Correct drainage or compaction before planting or aerating', priority: 'high' },
    { task: 'Wait for active growth before routine mowing', priority: 'medium' },
  ],
  // April
  [
    { task: 'Begin mowing at 1 to 2 inches when growth is active', priority: 'high' },
    { task: 'Remove no more than one-third of the leaf blade', priority: 'high' },
    { task: 'Use rainfall as the water guide instead of a fixed schedule', priority: 'medium' },
  ],
  // May
  [
    { task: 'Continue mowing at 1 to 2 inches', priority: 'high' },
    { task: 'Check stolons and seedheads when confirming the grass identity', priority: 'medium' },
    { task: 'Apply nitrogen only if the soil test and turf condition support it', priority: 'medium' },
  ],
  // June
  [
    { task: 'Mow as needed to manage fast growth and visible seedheads', priority: 'high' },
    { task: 'Keep each nitrogen application at or below 0.5 lb per 1,000 sq ft', priority: 'high' },
    { task: 'Irrigate only during drought or visible stress when rainfall is insufficient', priority: 'medium' },
  ],
  // July
  [
    { task: 'Maintain a 1 to 2 inch mowing height', priority: 'high' },
    { task: 'Keep the annual nitrogen total at or below 1 lb per 1,000 sq ft', priority: 'high' },
    { task: 'Do not add irrigation to soil that is already wet or saturated', priority: 'medium' },
  ],
  // August
  [
    { task: 'Continue mowing before seedheads become tall and coarse', priority: 'high' },
    { task: 'Inspect brown or thin areas for drainage, scalping, or leaf spot', priority: 'medium' },
    { task: 'Aerate only compacted areas during active growth', priority: 'low' },
  ],
  // September
  [
    { task: 'Reduce mowing as warm-season growth slows', priority: 'medium' },
    { task: 'Do not add nitrogen after the active midsummer feeding window', priority: 'high' },
    { task: 'Clear leaves before they block the carpetgrass canopy', priority: 'medium' },
  ],
  // October
  [
    { task: 'Mow only while the turf is actively growing', priority: 'medium' },
    { task: 'Check wet areas after rain and correct persistent standing water', priority: 'high' },
    { task: 'Avoid routine fertilizer and herbicide applications', priority: 'high' },
  ],
  // November
  [
    { task: 'Remove leaves and debris from the lawn', priority: 'medium' },
    { task: 'Minimize traffic on wet or dormant turf', priority: 'high' },
    { task: 'Use irrigation only if drought stress persists while turf is active', priority: 'low' },
  ],
  // December
  [
    { task: 'Leave dormant turf undisturbed and keep the surface clear', priority: 'medium' },
    { task: 'Do not apply nitrogen to dormant carpetgrass', priority: 'high' },
    { task: 'Review drainage and mowing notes before spring growth returns', priority: 'low' },
  ],
]

// AI grass-ID output uses 'St. Augustine' but the task table is keyed by
// 'St. Augustinegrass'. Other consumers (home email gate, care plan email)
// share this map so the normalization lives in one place.
export const MONTHLY_TASKS_KEY: Record<string, string> = {
  'St. Augustine': 'St. Augustinegrass',
  // Common model-output variants for the 2026-07 grass-type additions.
  'Kikuyu': 'Kikuyugrass',
  'Kikuyu Grass': 'Kikuyugrass',
  'Annual Rye': 'Annual Ryegrass',
  'Italian Ryegrass': 'Annual Ryegrass',
  'Cool-Season Mix': 'Mixed Cool-Season Blend',
  'Cool-Season Blend': 'Mixed Cool-Season Blend',
  'Mixed Cool Season Blend': 'Mixed Cool-Season Blend',
}

export function normalizeGrassKey(grassKey: string | undefined): string | undefined {
  if (!grassKey) return undefined
  return MONTHLY_TASKS_KEY[grassKey] ?? grassKey
}

export function getMonthlyTasks(grassKey: string, monthIndex?: number): MonthlyTask[] {
  const index = monthIndex ?? getCurrentMonthIndex()
  const tasks = MONTHLY_TASKS[normalizeGrassKey(grassKey) ?? grassKey]
  if (!tasks) return []
  return tasks[index] || []
}

export function getAllMonthlyTasks(grassKey: string | undefined): MonthlyTask[][] | null {
  const key = normalizeGrassKey(grassKey)
  if (!key) return null
  return MONTHLY_TASKS[key] ?? null
}

export function getTotalTaskCount(grassKey: string): number {
  const tasks = MONTHLY_TASKS[normalizeGrassKey(grassKey) ?? grassKey]
  if (!tasks) return 0
  return tasks.reduce((sum, month) => sum + month.length, 0)
}

export type { MonthlyTask }
